import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { selectCanAllMembersExportPointCloud } from "@/store/subscription-features/subscription-features-selectors";
import {
  selectHasUserProjectRole,
  selectHasWritePermission,
} from "@/store/user-selectors";
import { useAuthContext } from "@faro-lotv/gate-keepers";

type ExportModePermissions = {
  /** True if at least one export tool is available */
  canUseExportMode: boolean;

  /** True if the point cloud volume export is available */
  canUsePointCloudExport: boolean;

  /** True if the ortho photo export is available */
  canUseOrthoPhotoExport: boolean;

  /** True if the scans export is available */
  canUseScansExport: boolean;

  /** A message informing the user, about which permissions are missing to access the export tool. */
  requiredPermissionsMessage?: string;
};

/** @returns whether the current user is allowed to use the export mode */
export function useExportModePermissions(): ExportModePermissions {
  // The point cloud export requires a userId to be started
  const { isLoggedIn } = useAuthContext();

  // Everyone with write permissions is allowed to export
  const hasWritePermission = useAppSelector(selectHasWritePermission);

  // Users with read permissions can export, when it has been enabled for the company
  // We also allow the orthophoto export when the point cloud export is allowed.
  const canAllMembersExportPointCloud = useAppSelector(
    selectCanAllMembersExportPointCloud,
  );

  // We need this flag to allow our third part supplier of generated floor plans to access
  // the ortho photo export.
  const allowViewerExport = useAppSelector((state) =>
    selectHasFeature(Features.AllowViewerExport)(state),
  );

  // Anonymous access does not work for the export backend. We need a user that has at least some permission in a project.
  const hasProjectRole = useAppSelector(selectHasUserProjectRole);
  const canUseViewerExport = allowViewerExport && hasProjectRole;

  const hasExportPermission =
    isLoggedIn && (hasWritePermission || canAllMembersExportPointCloud);

  // The export for viewers is currently only supported for point cloud exports in the backend.
  const canUsePointCloudExport = hasExportPermission || canUseViewerExport;
  const canUseOrthoPhotoExport = hasExportPermission;
  const canUseExportMode = canUsePointCloudExport || canUseOrthoPhotoExport;
  const canUseScansExport = hasExportPermission;

  const requiredPermissionsMessage = allowViewerExport
    ? "Export tool is only accessible by project viewers"
    : "Export tool is only accessible with edit permissions";

  return {
    canUseScansExport,
    canUseOrthoPhotoExport,
    canUsePointCloudExport,
    canUseExportMode,
    requiredPermissionsMessage: canUseExportMode
      ? undefined
      : requiredPermissionsMessage,
  };
}
